/**
 * src/v2/services/vaultService.ts
 * GeoVault V2 frontend service.
 *
 * All country inputs accept alpha-2 ("US") or numeric (840) — both work.
 * Amounts are in stroops (1 XLM = 10_000_000 stroops).
 */

import { V2_API, resolveCountry } from '../config';
import type {
  BalanceResponse,
  TotalBalanceResponse,
  ApiResult,
} from '../types';

// ── Deposit ───────────────────────────────────────────────────────────────────

export interface DepositParams {
  userAddress:    string;
  country:        string | number;
  assetAddress:   string;
  amount:         string | number;   // stroops
  /** Pre-signed XDR from Freighter — fastest path */
  signedXdr?:     string;
  /** WebAuthn path */
  webauthnSignature?:         string;  // base64
  webauthnAuthenticatorData?: string;  // base64
  webauthnClientData?:        string;  // UTF-8
  signaturePayload?:          string;
  /** Legacy secret key path (dev only) */
  userSecretKey?: string;
}

export async function deposit(params: DepositParams): Promise<ApiResult> {
  const numericCountry = resolveCountry(params.country);
  const resp = await fetch(`${V2_API}/vault/deposit`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ ...params, country: numericCountry }),
  });
  return resp.json();
}

// ── Withdraw ──────────────────────────────────────────────────────────────────

export interface WithdrawParams {
  userAddress:   string;
  country:       string | number;
  assetAddress:  string;
  amount:        string | number;
  signedXdr?:    string;
  webauthnSignature?:         string;
  webauthnAuthenticatorData?: string;
  webauthnClientData?:        string;
  signaturePayload?:          string;
  userSecretKey?: string;
}

export async function withdraw(params: WithdrawParams): Promise<ApiResult> {
  const numericCountry = resolveCountry(params.country);
  const resp = await fetch(`${V2_API}/vault/withdraw`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ ...params, country: numericCountry }),
  });
  return resp.json();
}

// ── Cross-country transfer ────────────────────────────────────────────────────

export interface TransferParams {
  userAddress:   string;
  fromCountry:   string | number;
  toCountry:     string | number;
  assetAddress:  string;
  amount:        string | number;
  signedXdr?:    string;
  userSecretKey?: string;
}

export async function transferBetweenCountries(params: TransferParams): Promise<ApiResult> {
  const resp = await fetch(`${V2_API}/vault/transfer`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...params,
      fromCountry: resolveCountry(params.fromCountry),
      toCountry:   resolveCountry(params.toCountry),
    }),
  });
  return resp.json();
}

// ── In-country payment ────────────────────────────────────────────────────────

export interface PaymentParams {
  fromAddress:   string;
  toAddress:     string;
  country:       string | number;
  assetAddress:  string;
  amount:        string | number;
  signedXdr?:    string;
  userSecretKey?: string;
}

export async function executePayment(params: PaymentParams): Promise<ApiResult> {
  const resp = await fetch(`${V2_API}/vault/payment`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...params, country: resolveCountry(params.country) }),
  });
  return resp.json();
}

// ── Balance reads ─────────────────────────────────────────────────────────────

export async function getBalance(
  userAddress: string,
  country: string | number,
  assetAddress: string,
): Promise<BalanceResponse> {
  const numericCountry = resolveCountry(country);
  const url = new URL(`${V2_API}/vault/balance`);
  url.searchParams.set('userAddress',  userAddress);
  url.searchParams.set('country',      String(numericCountry));
  url.searchParams.set('assetAddress', assetAddress);

  const resp = await fetch(url.toString());
  if (!resp.ok) throw new Error(`Balance fetch failed: ${resp.status}`);
  return resp.json();
}

export async function getTotalVaultBalance(
  country: string | number,
  assetAddress: string,
): Promise<TotalBalanceResponse> {
  const numericCountry = resolveCountry(country);
  const url = new URL(`${V2_API}/vault/total-balance`);
  url.searchParams.set('country',      String(numericCountry));
  url.searchParams.set('assetAddress', assetAddress);

  const resp = await fetch(url.toString());
  if (!resp.ok) throw new Error(`Total balance fetch failed: ${resp.status}`);
  return resp.json();
}

/** Convert stroops to display amount with up to 7 decimal places */
export function stroopsToDisplay(stroops: string | number, decimals = 7): string {
  const big = BigInt(stroops);
  const factor = BigInt(10 ** decimals);
  const whole  = big / factor;
  const frac   = big % factor;
  return `${whole}.${frac.toString().padStart(decimals, '0')}`;
}

/** Convert display amount (e.g. "1.5") to stroops string */
export function displayToStroops(display: string, decimals = 7): string {
  const [whole, frac = ''] = display.split('.');
  const fracPadded = frac.padEnd(decimals, '0').slice(0, decimals);
  return String(BigInt(whole) * BigInt(10 ** decimals) + BigInt(fracPadded));
}

// ── Passkey ───────────────────────────────────────────────────────────────────

export async function registerPasskey(
  userAddress: string,
  pubkeyBase64: string,
  signedXdr?: string,
): Promise<ApiResult> {
  const resp = await fetch(`${V2_API}/vault/register-passkey`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ userAddress, pubkeyBase64, signedXdr }),
  });
  return resp.json();
}

export async function getPasskey(userAddress: string): Promise<string | null> {
  const url = new URL(`${V2_API}/vault/passkey`);
  url.searchParams.set('userAddress', userAddress);
  const resp = await fetch(url.toString());
  if (!resp.ok) return null;
  const data = await resp.json();
  return data.passkeyHex ?? null;
}
